import { useEffect, useMemo, useState, useCallback } from "react";
import {
  Kanban, Users2, DollarSign, ArrowRightLeft, ArrowLeftRight,
  Circle, CheckCircle2, ClipboardList, Loader2, Star, ShieldCheck,
  ChevronRight, ChevronLeft, Info, Wallet, UserPlus, Download,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { downloadCSV } from "@/lib/csv";
import AdminNav from "@/components/AdminNav";

const CLP = (n) => new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(Number(n || 0));
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("es-CL", { day: "2-digit", month: "short", year: "numeric" }) : "—");

const COLUMNS = [
  { key: "pending",   title: "Pendiente de asignar", accent: "amber",   icon: Circle },
  { key: "assigned",  title: "Asignada",             accent: "cyan",    icon: ClipboardList },
  { key: "completed", title: "Completada",           accent: "emerald", icon: CheckCircle2 },
];

const TABS = [
  { key: "requests",     label: "Solicitudes",           icon: Kanban },
  { key: "professionals",label: "Abogados y Mediadores", icon: Users2 },
  { key: "payments",     label: "Pagos",                 icon: Wallet },
];

const ACCENT_CLASS = {
  amber:   { border: "border-amber-400/40",   dot: "bg-amber-400",   ring: "ring-amber-400/40",   text: "text-amber-300"   },
  cyan:    { border: "border-cyan-400/40",    dot: "bg-cyan-400",    ring: "ring-cyan-400/40",    text: "text-cyan-300"    },
  emerald: { border: "border-emerald-400/40", dot: "bg-emerald-400", ring: "ring-emerald-400/40", text: "text-emerald-300" },
};

export default function AdminCRM() {
  const [tab, setTab] = useState("requests");
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/crm/summary");
      setSummary(data);
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  return (
    <div className="container-app py-8" data-testid="admin-crm-page">
      <AdminNav />
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow text-amber-300">Administración</div>
          <h1 className="serif text-4xl mt-1">CRM interno</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            Panel privado: métricas, tablero kanban de solicitudes, listado de profesionales y control de pagos.
            No visible para usuarios públicos.
          </p>
        </div>
      </div>

      {/* ── Métricas ────────────────────────────────────────────── */}
      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="crm-stats">
        <StatCard
          label="Total solicitudes" value={summary?.total_requests ?? "—"}
          icon={ClipboardList} accent="cyan" testid="crm-stat-total"
        />
        <StatCard
          label="Sin asignar" value={summary?.unassigned_requests ?? "—"}
          icon={Circle} accent="amber" testid="crm-stat-unassigned"
        />
        <StatCard
          label="Monto por pagar" value={CLP(summary?.amount_pending_clp || 0)}
          icon={DollarSign} accent="amber" testid="crm-stat-pending"
        />
        <StatCard
          label="Monto ya pagado" value={CLP(summary?.amount_paid_clp || 0)}
          icon={CheckCircle2} accent="emerald" testid="crm-stat-paid"
        />
      </div>

      {/* ── Tabs ────────────────────────────────────────────────── */}
      <nav className="mt-8 flex flex-wrap items-center gap-1.5 border-b border-white/5 pb-3">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              tab === key ? "bg-cyan-400/15 border border-cyan-400/40 text-cyan-100"
                          : "text-muted-foreground hover:text-foreground border border-transparent hover:border-white/10"
            }`}
            data-testid={`crm-tab-${key}`}
          >
            <Icon size={13}/> {label}
          </button>
        ))}
      </nav>

      {error && <div className="mt-4 text-sm text-[hsl(var(--destructive))]">{error}</div>}

      <div className="mt-5">
        {tab === "requests"      && <RequestsBoard onChange={loadSummary} />}
        {tab === "professionals" && <ProfessionalsList />}
        {tab === "payments"      && <PaymentsList onChange={loadSummary} />}
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════
 *  Stat card
 * ═════════════════════════════════════════════════════════════ */
function StatCard({ label, value, icon: Icon, accent, testid }) {
  const a = ACCENT_CLASS[accent] || ACCENT_CLASS.cyan;
  return (
    <div className={`panel p-4 ${a.border}`} data-testid={testid}>
      <div className={`inline-flex items-center gap-1.5 text-xs ${a.text} font-medium`}>
        <Icon size={13}/> {label}
      </div>
      <div className="serif text-3xl mt-2">{value}</div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════
 *  Kanban de Solicitudes con drag-and-drop nativo
 * ═════════════════════════════════════════════════════════════ */
function RequestsBoard({ onChange }) {
  const [items, setItems] = useState([]);
  const [pros, setPros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dragging, setDragging] = useState(null); // {kind, id}
  const [movingId, setMovingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [req, prof] = await Promise.all([
        api.get("/admin/crm/requests"),
        api.get("/admin/crm/professionals"),
      ]);
      setItems(req.data.items || []);
      setPros(prof.data.items || []);
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const byColumn = useMemo(() => {
    const g = { pending: [], assigned: [], completed: [] };
    items.forEach((it) => { (g[it.column] || g.pending).push(it); });
    return g;
  }, [items]);

  const moveTo = async (item, column) => {
    if (!item || item.column === column) return;
    setMovingId(item.id);
    // Optimistic update
    setItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, column } : x)));
    try {
      await api.post(`/admin/crm/requests/${item.kind}/${item.id}/move`, { column });
      onChange?.();
    } catch (e) {
      // Revertir
      setItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, column: item.column } : x)));
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setMovingId(null); }
  };

  const assignTo = async (item, professionalId) => {
    if (!item || !professionalId) return;
    setMovingId(item.id);
    try {
      const { data } = await api.post(
        `/admin/crm/requests/${item.kind}/${item.id}/assign`,
        { professional_id: professionalId },
      );
      // Optimistic: mueve a "assigned" y setea el nombre devuelto
      setItems((prev) => prev.map((x) => (
        x.id === item.id ? { ...x, column: "assigned", assignee_name: data.assignee_name } : x
      )));
      onChange?.();
      // Refrescar contador de solicitudes tomadas del profesional
      api.get("/admin/crm/professionals").then(({ data: d }) => setPros(d.items || [])).catch(() => {});
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setMovingId(null); }
  };

  if (loading) return <div className="py-16 text-center text-muted-foreground"><Loader2 size={20} className="mx-auto animate-spin"/> Cargando…</div>;
  if (items.length === 0) return (
    <div className="panel p-8 text-center" data-testid="crm-requests-empty">
      <ClipboardList size={28} className="mx-auto text-muted-foreground"/>
      <p className="mt-3 text-sm text-muted-foreground">Aún no hay solicitudes en el sistema.</p>
    </div>
  );

  return (
    <>
      {error && <div className="mb-3 text-sm text-[hsl(var(--destructive))]" data-testid="crm-requests-error">{error}</div>}
      <div className="grid lg:grid-cols-3 gap-4" data-testid="crm-kanban">
        {COLUMNS.map((col) => {
          const a = ACCENT_CLASS[col.accent];
          const list = byColumn[col.key] || [];
          return (
            <div key={col.key}
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
              onDrop={(e) => {
                e.preventDefault();
                if (dragging) { const it = items.find((x) => x.id === dragging.id); if (it) moveTo(it, col.key); }
                setDragging(null);
              }}
              className={`panel p-4 min-h-[400px] ${a.border} border-dashed`}
              data-testid={`crm-column-${col.key}`}
            >
              <div className="flex items-center justify-between">
                <div className={`inline-flex items-center gap-1.5 text-xs ${a.text} font-semibold uppercase tracking-wider`}>
                  <col.icon size={13}/> {col.title}
                </div>
                <span className="text-xs text-muted-foreground">{list.length}</span>
              </div>

              <div className="mt-3 space-y-3">
                {list.map((it) => (
                  <RequestCard
                    key={it.id}
                    item={it}
                    accent={col.accent}
                    isMoving={movingId === it.id}
                    pros={pros}
                    onDragStart={() => setDragging({ id: it.id, kind: it.kind })}
                    onDragEnd={() => setDragging(null)}
                    onMove={(target) => moveTo(it, target)}
                    onAssign={(pid) => assignTo(it, pid)}
                  />
                ))}
                {list.length === 0 && (
                  <div className="text-xs text-muted-foreground/60 py-8 text-center border border-dashed border-white/5 rounded-md">
                    Arrastra tarjetas aquí
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <p className="mt-4 text-xs text-muted-foreground inline-flex items-center gap-1.5">
        <Info size={11}/> Arrastra una tarjeta entre columnas o usa las flechas para moverla. Los cambios se guardan en tiempo real.
      </p>
    </>
  );
}

function RequestCard({ item, accent, onDragStart, onDragEnd, onMove, onAssign, isMoving, pros }) {
  const a = ACCENT_CLASS[accent];
  const columnIdx = COLUMNS.findIndex((c) => c.key === item.column);
  const prevKey = columnIdx > 0 ? COLUMNS[columnIdx - 1].key : null;
  const nextKey = columnIdx < COLUMNS.length - 1 ? COLUMNS[columnIdx + 1].key : null;

  // Sólo pendientes muestran selector de asignación.
  // Filtramos profesionales por el rol correcto y por región cuando coincide (arriba de la lista).
  const assignable = useMemo(() => {
    if (item.column !== "pending" || !pros) return [];
    const role = item.kind === "delegation" ? "lawyer" : "mediator";
    const filtered = pros.filter((p) => p.role === role);
    // Ordena: misma región primero, luego por reputación desc
    return [...filtered].sort((x, y) => {
      const rx = x.region === item.region ? 0 : 1;
      const ry = y.region === item.region ? 0 : 1;
      if (rx !== ry) return rx - ry;
      return (y.reputation || 0) - (x.reputation || 0);
    });
  }, [pros, item.column, item.kind, item.region]);

  const [pickerOpen, setPickerOpen] = useState(false);

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={`p-3 rounded-lg border ${a.border} bg-white/[0.02] hover:bg-white/[0.04] transition-colors cursor-grab active:cursor-grabbing ${isMoving ? "opacity-60" : ""}`}
      data-testid={`crm-card-${item.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">{item.title}</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            {item.kind === "delegation" ? "Delegación" : "Mediación"}
          </div>
        </div>
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full bg-white/5 border ${a.border} ${a.text} shrink-0`}>
          {CLP(item.amount_clp)}
        </span>
      </div>
      <div className="mt-2 text-xs text-muted-foreground space-y-0.5">
        {item.court && item.court !== "—" && <div>🏛️ {item.court}</div>}
        <div>👤 Solicita: <span className="text-foreground">{item.requester_name}</span></div>
        <div>
          {item.assignee_name
            ? <>✍️ Asignado a: <span className="text-foreground">{item.assignee_name}</span></>
            : <span className="text-amber-300/70">Sin asignar</span>}
        </div>
        <div>📅 {fmtDate(item.date)}</div>
      </div>

      {/* Asignador directo — sólo en columna "pending" */}
      {item.column === "pending" && (
        <div className="mt-2.5" data-testid={`crm-assign-block-${item.id}`}>
          {!pickerOpen ? (
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              className="w-full text-[11px] py-1.5 px-2 rounded-md border border-cyan-400/40 text-cyan-100 bg-cyan-400/[0.06] hover:bg-cyan-400/[0.12] inline-flex items-center justify-center gap-1"
              data-testid={`crm-assign-open-${item.id}`}
            >
              <UserPlus size={11}/> Asignar a {item.kind === "delegation" ? "abogado" : "mediador"}…
            </button>
          ) : (
            <div className="flex items-center gap-1">
              <select
                defaultValue=""
                onChange={(e) => { const v = e.target.value; if (v) { onAssign(v); setPickerOpen(false); } }}
                className="field flex-1 text-[11px] py-1"
                disabled={isMoving}
                data-testid={`crm-assign-select-${item.id}`}
              >
                <option value="" disabled>
                  Elige {item.kind === "delegation" ? "abogado" : "mediador"}…
                </option>
                {assignable.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} · {p.comuna}
                    {p.region === item.region ? " · misma región" : ""}
                    {" · ★ "}{(p.reputation || 0).toFixed(1)}
                  </option>
                ))}
                {assignable.length === 0 && <option value="" disabled>Sin profesionales disponibles</option>}
              </select>
              <button
                type="button"
                onClick={() => setPickerOpen(false)}
                className="text-[10px] px-2 py-1 rounded-md text-muted-foreground hover:text-foreground"
                aria-label="Cancelar"
              >
                ✕
              </button>
            </div>
          )}
        </div>
      )}

      <div className="mt-3 flex items-center justify-between gap-1">
        <button
          type="button"
          disabled={!prevKey || isMoving}
          onClick={() => prevKey && onMove(prevKey)}
          className="btn-ghost text-[10px] py-1 px-2 disabled:opacity-30"
          aria-label="Mover a la columna anterior"
          data-testid={`crm-card-${item.id}-prev`}
        >
          <ChevronLeft size={11}/>
        </button>
        <span className="text-[10px] text-muted-foreground">{item.region || ""}</span>
        <button
          type="button"
          disabled={!nextKey || isMoving}
          onClick={() => nextKey && onMove(nextKey)}
          className="btn-ghost text-[10px] py-1 px-2 disabled:opacity-30"
          aria-label="Mover a la columna siguiente"
          data-testid={`crm-card-${item.id}-next`}
        >
          <ChevronRight size={11}/>
        </button>
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════
 *  Listado de profesionales
 * ═════════════════════════════════════════════════════════════ */
function ProfessionalsList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all"); // all | lawyer | mediator

  useEffect(() => {
    api.get("/admin/crm/professionals")
      .then(({ data }) => setItems(data.items || []))
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, []);

  const list = useMemo(() => (
    filter === "all" ? items : items.filter((p) => p.role === filter)
  ), [items, filter]);

  if (loading) return <div className="py-16 text-center text-muted-foreground"><Loader2 size={20} className="mx-auto animate-spin"/></div>;

  return (
    <div>
      {error && <div className="mb-3 text-sm text-[hsl(var(--destructive))]">{error}</div>}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          {[["all", "Todos"], ["lawyer", "Abogados"], ["mediator", "Mediadores"]].map(([k, label]) => (
            <button key={k} onClick={() => setFilter(k)}
              className={`px-2.5 py-1 rounded-md text-xs border ${filter === k ? "bg-cyan-400/10 border-cyan-400/40 text-cyan-100" : "border-white/10 text-muted-foreground hover:text-foreground"}`}
              data-testid={`crm-pros-filter-${k}`}
            >
              {label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => downloadCSV(
            `profesionales-colegalink-${new Date().toISOString().slice(0, 10)}.csv`,
            list,
            [
              { key: "name", label: "Nombre" },
              { key: "email", label: "Email" },
              { key: "type", label: "Tipo" },
              { key: "region", label: "Región" },
              { key: "comuna", label: "Comuna" },
              { key: "reputation", label: "Calificación", format: (v) => (Number(v || 0)).toFixed(2).replace(".", ",") },
              { key: "requests_taken", label: "Solicitudes tomadas" },
              { key: "verified", label: "Verificado", format: (v) => (v ? "sí" : "no") },
              { key: "plan", label: "Plan" },
            ],
          )}
          disabled={list.length === 0}
          className="btn-ghost text-xs py-1 px-2 border border-white/10 inline-flex items-center gap-1.5 disabled:opacity-40"
          data-testid="crm-pros-export"
        >
          <Download size={12}/> Exportar CSV
        </button>
      </div>

      <div className="panel overflow-hidden" data-testid="crm-professionals-table">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.03] text-[11px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left px-3 py-2">Nombre</th>
              <th className="text-left px-3 py-2">Tipo</th>
              <th className="text-left px-3 py-2">Comuna</th>
              <th className="text-left px-3 py-2">Calificación</th>
              <th className="text-right px-3 py-2">Solicitudes tomadas</th>
            </tr>
          </thead>
          <tbody>
            {list.map((p) => (
              <tr key={p.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`crm-pro-${p.id}`}>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span>{p.name}</span>
                    {p.verified && <ShieldCheck size={12} className="text-emerald-300" title="Verificado"/>}
                  </div>
                  <div className="text-[11px] text-muted-foreground">{p.email}</div>
                </td>
                <td className="px-3 py-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border ${p.role === "lawyer" ? "border-cyan-400/40 text-cyan-200 bg-cyan-400/5" : "border-emerald-400/40 text-emerald-200 bg-emerald-400/5"}`}>
                    {p.type}
                  </span>
                </td>
                <td className="px-3 py-2 text-muted-foreground">{p.comuna}</td>
                <td className="px-3 py-2">
                  <div className="inline-flex items-center gap-1">
                    <Star size={11} className="text-amber-300"/>
                    <span>{(p.reputation || 0).toFixed(1)}</span>
                  </div>
                </td>
                <td className="px-3 py-2 text-right font-semibold">{p.requests_taken}</td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">Sin resultados</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════
 *  Listado de pagos
 * ═════════════════════════════════════════════════════════════ */
function PaymentsList({ onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [markingId, setMarkingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/admin/crm/payments");
      setItems(data.items || []);
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markPaid = async (p) => {
    if (!window.confirm(`¿Marcar como PAGADO el cobro de ${CLP(p.amount_clp)} a ${p.payee_name}?`)) return;
    setMarkingId(p.id);
    try {
      await api.post(`/admin/crm/payments/${p.id}/mark-paid`, {});
      await load();
      onChange?.();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setMarkingId(null); }
  };

  if (loading) return <div className="py-16 text-center text-muted-foreground"><Loader2 size={20} className="mx-auto animate-spin"/></div>;
  if (items.length === 0) return (
    <div className="panel p-8 text-center" data-testid="crm-payments-empty">
      <Wallet size={28} className="mx-auto text-muted-foreground"/>
      <p className="mt-3 text-sm text-muted-foreground">Aún no hay pagos registrados.</p>
    </div>
  );

  return (
    <div>
      {error && <div className="mb-3 text-sm text-[hsl(var(--destructive))]">{error}</div>}
      <div className="panel overflow-hidden" data-testid="crm-payments-table">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.03] text-[11px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left px-3 py-2">Pagador</th>
              <th className="text-left px-3 py-2">Beneficiario</th>
              <th className="text-right px-3 py-2">Monto</th>
              <th className="text-left px-3 py-2">Estado</th>
              <th className="text-left px-3 py-2">Fecha</th>
              <th className="text-right px-3 py-2">Acción</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`crm-pay-${p.id}`}>
                <td className="px-3 py-2">{p.payer_name}</td>
                <td className="px-3 py-2">{p.payee_name}</td>
                <td className="px-3 py-2 text-right font-semibold">{CLP(p.amount_clp)}</td>
                <td className="px-3 py-2">
                  {p.status === "pagado" ? (
                    <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-400/10 border border-emerald-400/40 text-emerald-200">
                      <CheckCircle2 size={11}/> Pagado
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-400/10 border border-amber-400/40 text-amber-200">
                      <Circle size={9}/> Pendiente
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-muted-foreground text-xs">
                  {fmtDate(p.paid_at || p.created_at)}
                </td>
                <td className="px-3 py-2 text-right">
                  {p.status === "pendiente" ? (
                    <button
                      type="button"
                      onClick={() => markPaid(p)}
                      disabled={markingId === p.id}
                      className="btn-ghost text-[11px] py-1 px-2 border-emerald-400/40 text-emerald-200 hover:bg-emerald-400/10 disabled:opacity-50"
                      data-testid={`crm-pay-mark-${p.id}`}
                    >
                      {markingId === p.id ? "Marcando…" : "Marcar pagado"}
                    </button>
                  ) : (
                    <span className="text-[11px] text-muted-foreground">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
